import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhPlusCircle";

import { rpc } from "ui-common/services/rpc";
import { type ContentTheme } from "api-contracts/rpc/content-themes";

import { styles } from "./theme-selection.css";
import { repeat } from "lit/directives/repeat.js";

@customElement("theme-selection")
export class ThemeSelection extends LitElement {
  static override styles = [styles];

  @state()
  private themes: ContentTheme[] = [];

  @state()
  private loading = false;

  @state()
  private error: string | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this.loadThemes();
  }

  private async loadThemes() {
    this.loading = true;
    this.error = null;

    try {
      const result = await rpc("content-themes", {});
      this.themes = result.categories;
    } catch (error) {
      console.error("Failed to load content categories:", error);
      this.error = "Failed to load themes";
    } finally {
      this.loading = false;
    }
  }

  private handleThemeClick(themeId: string) {
    if (this.selectedThemes.has(themeId)) {
      this.selectedThemes.delete(themeId);
    } else {
      this.selectedThemes.add(themeId);
    }
    this.dispatchEvent(
      new CustomEvent("themes-selected", {
        detail: { themes: Array.from(this.selectedThemes) },
      }),
    );

    this.requestUpdate("selectedThemes");
  }

  @state()
  selectedThemes = new Set<string>();

  private renderTheme(theme: ContentTheme) {
    const isSelected = this.selectedThemes.has(theme.id);

    return html`
      <div
        class="theme-pill ${isSelected ? "selected" : ""}"
        @click=${() => this.handleThemeClick(theme.id)}
        role="button"
        tabindex="0"
        aria-pressed=${isSelected}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.handleThemeClick(theme.id);
          }
        }}
      >
        ${isSelected ? html`<ph-check-circle class="checkmark" weight="fill"></ph-check-circle>` : nothing}

        <span class="theme-text">${theme.name}</span>
      </div>
    `;
  }

  protected override render() {
    if (this.loading) {
      return html`<div class="themes-container">Loading themes...</div>`;
    }

    if (this.error) {
      return html`<div class="themes-container">Error: ${this.error}</div>`;
    }

    return html`<div class="themes-container">
      ${repeat(
        this.themes,
        (x) => x.id + this.selectedThemes.has(x.id),
        (theme) => this.renderTheme(theme),
      )}
    </div>`;
  }
}
